import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Circle,
  Clock3,
  FolderKanban,
  List as ListIcon,
  Pencil,
  Plus,
  Ticket,
  Users,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { formatDisplayDateTime } from '@/lib/date-format';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useProject, useProjectDashboard } from '@/hooks/use-projects';
import { useSocket } from '@/hooks/use-socket';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { useUIStore } from '@/stores/ui-store';
import { ProjectSettingsDialog } from '@/components/projects/ProjectSettingsDialog';
import { CreateListDialog } from '@/features/lists';

export function ProjectDashboardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectId);
  const { data: dashboard, isLoading: dashboardLoading } = useProjectDashboard(projectId);
  const { data: capabilities } = useMyCapabilities();
  const { joinProject, leaveProject } = useSocket();
  const { createListOpen, createListProjectId, setCreateListOpen } = useUIStore();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const canCreateTask = Boolean(capabilities?.['task.create']);
  const canCreateList = Boolean(capabilities?.['list.create']);
  const canEditProject = Boolean(capabilities?.['project.edit']);
  const firstListId = dashboard?.lists[0]?.listId ?? null;
  const showCreateListDialog = Boolean(projectId && createListOpen && createListProjectId === projectId);
  const legacyListId = searchParams.get('listId')?.trim();

  useEffect(() => {
    if (project?.id && legacyListId) {
      const next = new URLSearchParams(searchParams);
      next.delete('listId');
      const query = next.toString();
      navigate(`/projects/${project.id}/lists/${legacyListId}${query ? `?${query}` : ''}`, {
        replace: true,
      });
    }
  }, [project?.id, legacyListId, navigate, searchParams]);

  useEffect(() => {
    if (!projectId) return;
    joinProject(projectId);
    return () => leaveProject(projectId);
  }, [projectId, joinProject, leaveProject]);

  const statusChartMax = useMemo(
    () => Math.max(1, ...(dashboard?.statusChart.map((item) => item.count) ?? [0])),
    [dashboard],
  );

  if (projectLoading || dashboardLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (legacyListId) {
    return null;
  }

  if (projectError || !project || !dashboard) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <FolderKanban className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">Project not found</p>
        <Button asChild variant="outline">
          <Link to="/projects">Back to Projects</Link>
        </Button>
      </div>
    );
  }

  const summaryCards = [
    { label: 'Total tickets', value: dashboard.summary.totalTickets },
    { label: 'Open', value: dashboard.summary.openTickets },
    { label: 'In progress', value: dashboard.summary.inProgressTickets },
    { label: 'Closed', value: dashboard.summary.closedTickets },
    { label: 'Overdue', value: dashboard.summary.overdueTickets },
    { label: 'Unassigned', value: dashboard.summary.unassignedTickets },
  ];

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <Card>
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: project.color }} />
                  <span className="truncate">{dashboard.projectName}</span>
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <span>ID: {dashboard.projectIdentifier}</span>
                  {project.description ? <span className="truncate">- {project.description}</span> : null}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {project.members?.length ?? 0} members
                </Badge>
                {canCreateTask && firstListId ? (
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate(`/projects/${project.id}/lists/${firstListId}`)}
                  >
                    <Ticket className="h-4 w-4" />
                    New ticket
                  </Button>
                ) : null}
                {canCreateList ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="gap-2"
                    onClick={() => setCreateListOpen(true, project.id)}
                  >
                    <Plus className="h-4 w-4" />
                    New list
                  </Button>
                ) : null}
                {canEditProject ? (
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => setSettingsOpen(true)}>
                    <Pencil className="h-4 w-4" />
                    Edit project
                  </Button>
                ) : null}
                {firstListId ? (
                  <Button asChild size="sm" variant="outline" className="gap-2">
                    <Link to={`/projects/${project.id}/lists/${firstListId}`}>
                      <ListIcon className="h-4 w-4" />
                      Open full list view
                    </Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {summaryCards.map((item) => (
            <Card key={item.label}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="text-sm text-muted-foreground">{item.label}</div>
                <div className="text-2xl font-semibold tabular-nums">{item.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Card className="xl:col-span-2">
            <CardHeader>
              <CardTitle>Lists overview</CardTitle>
              <CardDescription>Click a list to open ticket view</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashboard.lists.length === 0 ? (
                <p className="text-sm text-muted-foreground">No visible lists in this project.</p>
              ) : (
                dashboard.lists.map((row) => (
                  <button
                    key={row.listId}
                    type="button"
                    className="w-full rounded-lg border bg-card px-4 py-3 text-left transition-colors hover:bg-accent/50"
                    onClick={() => navigate(`/projects/${project.id}/lists/${row.listId}`)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium">{row.listName}</p>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Last updated{' '}
                          {row.lastUpdated
                            ? formatDistanceToNow(new Date(row.lastUpdated), { addSuffix: true })
                            : 'n/a'}
                          {row.ownerTeamName ? ` - Team: ${row.ownerTeamName}` : ''}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {row.ticketCount}
                      </Badge>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded bg-muted/40 px-2 py-1 text-muted-foreground">
                        Open: <span className="font-medium text-foreground">{row.openCount}</span>
                      </div>
                      <div className="rounded bg-muted/40 px-2 py-1 text-muted-foreground">
                        In progress: <span className="font-medium text-foreground">{row.inProgressCount}</span>
                      </div>
                      <div className="rounded bg-muted/40 px-2 py-1 text-muted-foreground">
                        Closed: <span className="font-medium text-foreground">{row.closedCount}</span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Status workload</CardTitle>
              <CardDescription>Tickets by status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {dashboard.statusChart.length === 0 ? (
                <p className="text-sm text-muted-foreground">No status data yet.</p>
              ) : (
                dashboard.statusChart.map((item) => (
                  <div key={item.statusId} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Circle className="h-3 w-3 fill-current" style={{ color: item.color }} />
                        {item.statusName}
                      </span>
                      <span className="font-medium text-foreground tabular-nums">{item.count}</span>
                    </div>
                    <div className="h-2 rounded bg-muted">
                      <div
                        className="h-2 rounded"
                        style={{
                          width: `${Math.max(6, (item.count / statusChartMax) * 100)}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <CardDescription>Latest ticket and discussion changes in this project</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboard.recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent activity.</p>
            ) : (
              <div className="space-y-3">
                {dashboard.recentActivity.map((item, index) => (
                  <div key={item.id}>
                    {index > 0 ? <Separator className="mb-3" /> : null}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{item.actorName ?? 'System'}</span>{' '}
                          <span className="text-muted-foreground">
                            {item.type === 'task_created'
                              ? 'created ticket'
                              : item.type === 'comment_created'
                                ? 'commented on'
                                : item.type === 'status_changed'
                                  ? 'changed status on'
                                  : 'updated'}
                          </span>{' '}
                          <Link
                            to={`/projects/${project.id}/lists/${item.listId}`}
                            className="font-medium hover:underline"
                          >
                            {item.taskTitle}
                          </Link>
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.listName}
                          {item.details ? ` - ${item.details}` : ''}
                        </p>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                        <Clock3 className="h-3.5 w-3.5" />
                        {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })} (
                        {formatDisplayDateTime(item.timestamp)})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateListDialog
        projectId={project.id}
        open={showCreateListDialog}
        onOpenChange={(open) => setCreateListOpen(open, project.id)}
        onCreated={(newList) => navigate(`/projects/${project.id}/lists/${newList.id}`)}
      />

      {canEditProject ? (
        <ProjectSettingsDialog
          project={{
            id: project.id,
            name: project.name,
            description: project.description ?? undefined,
            color: project.color,
            icon: project.icon ?? undefined,
          }}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      ) : null}
    </div>
  );
}
